/**
 * InstructorSchedulingPrefsPage — quiet preferences page where
 * the instructor sets their operational baseline.
 *
 * Phase A — Preferences Foundation (Feb 18, 2026). Wired to
 * /api/scheduling/prefs (GET + PUT). No travel-buffer math
 * surfaced yet (that's Phase B).
 *
 * Doctrine sources:
 *   /app/memory/TRAVEL_AWARE_SCHEDULING_PROPOSAL.md §8.1
 *   /app/memory/TRAVEL_AWARE_WIREFRAME_PASS.md §1
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Settings, Check } from "lucide-react";
import { toast } from "sonner";
import TravelBufferSelfCheck from "../components/instructor/TravelBufferSelfCheck";

const API = process.env.REACT_APP_BACKEND_URL;

const DAYS = [
  { key: "mon", label: "Monday" },
  { key: "tue", label: "Tuesday" },
  { key: "wed", label: "Wednesday" },
  { key: "thu", label: "Thursday" },
  { key: "fri", label: "Friday" },
  { key: "sat", label: "Saturday" },
  { key: "sun", label: "Sunday" },
];

function PrefCard({ title, help, children, savedRecently }) {
  return (
    <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
      <header className="mb-4">
        <h2 className="text-base font-bold text-slate-900">{title}</h2>
        <p className="text-[12px] text-slate-500 mt-0.5">{help}</p>
      </header>
      {children}
      {savedRecently && (
        <p className="mt-3 text-[12px] text-emerald-600 inline-flex items-center gap-1">
          <Check className="w-3 h-3" />
          Saved
        </p>
      )}
    </section>
  );
}

function dayDisplay(day) {
  if (!day || day.off) return "off";
  return `${day.start} → ${day.end}`;
}

export default function InstructorSchedulingPrefsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [postcode, setPostcode] = useState("");
  const [bufferMinutes, setBufferMinutes] = useState(10);
  const [workingHours, setWorkingHours] = useState(null);
  const [travelAwareEnabled, setTravelAwareEnabled] = useState(false);
  const [savedFlag, setSavedFlag] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/scheduling/prefs`, {
          withCredentials: true,
        });
        if (!alive) return;
        setPostcode(r.data?.base_postcode || "");
        setBufferMinutes(Number(r.data?.buffer_minutes ?? 10));
        setWorkingHours(r.data?.working_hours || null);
        setTravelAwareEnabled(Boolean(r.data?.travel_aware_v1_enabled));
      } catch {
        // Calm-baseline: load fails → silently fall through to defaults.
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  async function save() {
    if (saving) return;
    setSaving(true);
    try {
      const body = {
        base_postcode: postcode.trim() || null,
        working_hours: workingHours || undefined,
        buffer_minutes: Number(bufferMinutes),
        travel_aware_v1_enabled: travelAwareEnabled,
      };
      const r = await axios.put(`${API}/api/scheduling/prefs`, body, {
        withCredentials: true,
      });
      setPostcode(r.data?.base_postcode || "");
      setBufferMinutes(Number(r.data?.buffer_minutes ?? 10));
      setWorkingHours(r.data?.working_hours || null);
      setTravelAwareEnabled(Boolean(r.data?.travel_aware_v1_enabled));
      setSavedFlag(true);
      setTimeout(() => setSavedFlag(false), 2500);
    } catch (err) {
      const detail = err?.response?.data?.detail
        || (Array.isArray(err?.response?.data?.detail) && err.response.data.detail[0]?.msg)
        || "Could not save preferences.";
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div
        data-testid="instructor-scheduling-prefs-page-loading"
        className="min-h-dvh bg-slate-50 flex items-center justify-center"
      >
        <p className="text-sm text-slate-400">Loading…</p>
      </div>
    );
  }

  return (
    <div
      data-testid="instructor-scheduling-prefs-page"
      className="min-h-dvh bg-slate-50"
    >
      <header className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          data-testid="scheduling-prefs-back-btn"
          className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center"
          aria-label="Back"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Settings className="w-4 h-4 text-slate-400" />
            <p className="text-base font-bold text-slate-900">
              Scheduling &amp; Travel Preferences
            </p>
          </div>
          <p className="text-[12px] text-slate-500 mt-0.5">
            Manage your operational baseline.
          </p>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-8">

        <PrefCard
          title="Base Postcode"
          help="Your starting point for the day."
          savedRecently={savedFlag}
        >
          <label className="block">
            <span className="sr-only">Base postcode</span>
            <div className="flex items-center gap-2">
              <input
                data-testid="prefs-base-postcode-input"
                type="text"
                value={postcode}
                onChange={(e) => setPostcode(e.target.value)}
                className="flex-1 text-sm text-slate-900 px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-300 tabular-nums uppercase"
                placeholder="SW19 1AA"
                autoCapitalize="characters"
              />
            </div>
          </label>
        </PrefCard>

        <PrefCard
          title="Working Hours"
          help="Your standard operational window."
        >
          <ul className="space-y-2">
            {DAYS.map((d) => (
              <li
                key={d.key}
                data-testid={`prefs-working-hours-row-${d.key}`}
                className="flex items-center justify-between text-sm"
              >
                <span className="text-slate-700">{d.label}</span>
                <span className="text-slate-500 tabular-nums">
                  {dayDisplay(workingHours?.[d.key])}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-[11px] text-slate-400">
            Per-day editing arrives in the next iteration.
          </p>
        </PrefCard>

        <PrefCard
          title="Default Travel Buffer"
          help="Extra time between lessons to avoid rushing."
        >
          <div className="flex items-center gap-2">
            <input
              data-testid="prefs-travel-buffer-input"
              type="number"
              min={0}
              max={60}
              value={bufferMinutes}
              onChange={(e) => setBufferMinutes(Number(e.target.value))}
              className="w-20 text-sm text-slate-900 px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-300 tabular-nums"
            />
            <span className="text-sm text-slate-500">minutes</span>
          </div>
        </PrefCard>

        <PrefCard
          title="Travel-aware preview"
          help="Show the travel summary + tight-gap warning when booking lessons. Off by default."
        >
          <label
            data-testid="prefs-travel-aware-toggle"
            className="flex items-center gap-3 cursor-pointer"
          >
            <input
              type="checkbox"
              checked={travelAwareEnabled}
              onChange={(e) => setTravelAwareEnabled(e.target.checked)}
              className="w-4 h-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900/10"
            />
            <span className="text-sm text-slate-700">
              Enable travel-aware preview
            </span>
          </label>
          <p className="mt-2 text-[11px] text-slate-400 leading-relaxed">
            When enabled, the booking flow shows estimated travel
            time between consecutive lessons + a calm warning if the
            gap is tight. You can always override. Nothing is
            auto-blocked or auto-moved.
          </p>
        </PrefCard>

        <TravelBufferSelfCheck />

        <div>
          <button
            type="button"
            data-testid="prefs-save-changes-btn"
            onClick={save}
            disabled={saving}
            className="text-sm font-bold text-white bg-slate-900 px-5 py-2.5 rounded-lg hover:bg-slate-800 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>

      </div>
    </div>
  );
}
